// node_modules
import { FC, useCallback, useContext, useEffect, useState } from "react";
import { faTrashAlt, faPencilAlt } from "@fortawesome/free-solid-svg-icons";
import { faCommentAlt} from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// Helpers
import { DateHelperSingleton, ToastHelperSingleton } from "Helpers";
// Types
import { TPassSideNotePost, TSideNoteDTO } from "Types";
// styles
import styles from "./sideNote.module.scss";
// Components
import { LetteredUserCircle, SaveableEditableMarkdown } from "Components";
import { CreateSideNoteReply } from "./CreateSideNoteReply";
// Enums
import { EntityTypeEnum, ToastTypeEnum } from "Enums";
// Controllers
import { SideNoteControllerSingleton } from "Controllers";
// Constants
import { HubConstants } from "Constants";
// Contexts
import { WebsocketContext } from "Providers";

type TSideNoteProps = {
    sideNote: TSideNoteDTO;
    isReply?: boolean;
    entityType: EntityTypeEnum,
    entityId: number | string,
    isReplyable: boolean,
    extraClassName?: string,
    sideNoteResourceName: string
}

export const SideNote: FC<TSideNoteProps> = ({ sideNote, isReply, entityType, entityId, isReplyable, extraClassName, sideNoteResourceName }: TSideNoteProps) => {
    // State
    const [isCurrentlyEditing, setIsCurrentlyEditing] = useState(false);
    const [isCurrentlyReplying, setIsCurrentlyReplying] = useState(false);

    // Logic
    const sideNoteDate = new Date(sideNote.dateCreated);
    const timeSincePublication = DateHelperSingleton.getTimeSinceDate(sideNoteDate);

    // Contexts
    const { webSocketController } = useContext(WebsocketContext);
    
    const getClassName = (): string => {
        const classNameList = [styles.mainCommentContainer];
        if (isReply) classNameList.push(styles.isReply);
        if (extraClassName) classNameList.push(extraClassName);
        return classNameList.join(" ");
    };

    const replyingDone = () => {
        // hide the reply form
        setIsCurrentlyReplying(false);
    };

    const stopReplying = () => {
        // hide the reply form
        setIsCurrentlyReplying(false);
    };

    const onDeleteSideNoteClick = async () => {
        if(!window.confirm("Do you really want to remove your side note? If you posted the first side note in the thread " + 
                "and people replied then their replies will also be removed.")) {
            return;
        }

        await SideNoteControllerSingleton.deleteAsync(sideNote);
    };

    const onSaveNewSideNoteText = async (text: string) => {
        sideNote.text = text;

        const isSuccess = await SideNoteControllerSingleton.updateAsync(sideNote);
        
        if(!isSuccess) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Was not able to save changes to comment.");
            return;
        }

        // stop claiming editing on the current side note
        webSocketController.invokeFunction(HubConstants.STOP_EDITING_DELIVERY_RESOURCE, sideNoteResourceName);
    };

    const startEditing = (): void => {
        // claim editing on the current side note
        webSocketController.invokeFunction(HubConstants.START_EDITING_DELIVERY_RESOURCE, sideNoteResourceName);
    };

    const sideNoteInfo: TPassSideNotePost = {
        entityId: entityId,
        entityType: entityType,
        isReply: true,
        noteType: sideNote.noteType,
        privacyLevel: sideNote.privacyLevel,
        replyToId: sideNote.id
    };

    const handleSideNoteAlreadyBeingEdited = useCallback((): void => {
        ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Somebody is already editing this side note.");
    }, []);

    const handleSideNoteEditStatusChanged = useCallback((newIsCurrentlyEditing: boolean): void => {
        setIsCurrentlyEditing(newIsCurrentlyEditing);
    }, []);

    useEffect(() => {
        webSocketController.addHandler(`${HubConstants.DELIVERY_RESOURCE_EDIT_STATUS_CHANGE_PREFIX}${sideNoteResourceName}`,
            handleSideNoteEditStatusChanged);
        webSocketController.addHandler(`${HubConstants.DELIVERY_RESOURCE_ALREADY_BEING_EDITED_PREFIX}${sideNoteResourceName}`, 
            handleSideNoteAlreadyBeingEdited);

            // on clean up time, send stop claiming edit message to prevent claiming lock 
            // and remove handlers
            return () => {
                webSocketController.invokeFunction(HubConstants.STOP_EDITING_DELIVERY_RESOURCE, sideNoteResourceName);
                webSocketController.removeHandler(`${HubConstants.DELIVERY_RESOURCE_ALREADY_BEING_EDITED_PREFIX}${sideNoteResourceName}`, 
                    handleSideNoteAlreadyBeingEdited);
                webSocketController.addHandler(`${HubConstants.DELIVERY_RESOURCE_EDIT_STATUS_CHANGE_PREFIX}${sideNoteResourceName}`,
                    handleSideNoteEditStatusChanged);
            };
    }, [webSocketController, sideNoteResourceName, handleSideNoteAlreadyBeingEdited, handleSideNoteEditStatusChanged]);

    return (
        <div className={getClassName()}>
            <div className={styles.commentContainer}>
                <div className={styles.userCircleContainer}>
                    <LetteredUserCircle key={sideNote.username} username={sideNote.username} />
                </div>
                <div className={styles.contentContainer}>
                    <div className={styles.contentHeader}>
                        <div className={styles.postedInfo}>
                            <span className={styles.postedText}>Posted</span>
                            <span className={styles.dateText}>{timeSincePublication}</span>
                        </div>
                        {isReplyable && sideNote.isOwnedByCurrentUser && !isCurrentlyEditing ?
                            <div className={styles.actionContainer}>
                                <FontAwesomeIcon icon={faPencilAlt} title={"Edit comment"} className={styles.editButton} onClick={startEditing} />
                                <FontAwesomeIcon icon={faTrashAlt} title={"Delete comment"} className={styles.deleteButton} onClick={onDeleteSideNoteClick} />
                            </div>
                            : <></>}
                    </div>
                    <SaveableEditableMarkdown 
                        isEditing={isCurrentlyEditing}
                        source={sideNote.text} 
                        noSource={"No notes available."}
                        onSave={onSaveNewSideNoteText} 
                        env={entityId ? { "technologyId": entityId } : undefined} 
                        resourceName={sideNoteResourceName} 
                        doLinkify={true} />
                </div>
            </div>
            {!isReply ?
                <>
                    {sideNote.replies.map((reply) => {
                        return (
                            <SideNote 
                                key={reply.id} 
                                sideNote={reply}
                                isReply={true} 
                                entityType={entityType} 
                                entityId={entityId} 
                                isReplyable={isReplyable} 
                                sideNoteResourceName={`${HubConstants.SIDE_NOTE_RESOURCE_NAME_PREFIX}${reply.id}`}/>
                        );
                    })}

                    {isReplyable && isCurrentlyReplying ?
                        <>
                            <CreateSideNoteReply sideNoteInfo={sideNoteInfo} onCancel={stopReplying} onDone={replyingDone} />
                        </>
                        : <></>}

                    {isReplyable && !isCurrentlyReplying ?
                        <div className={styles.sideNoteActions}>
                            <div onClick={() => { setIsCurrentlyReplying(true); }} className={styles.replyText}>
                                <FontAwesomeIcon icon={faCommentAlt} /> Reply
                            </div>
                        </div>
                        : <></>}
                </>
                : <> </>
            }
        </div>
    );
};